import { ContractExcelColumnsService } from '../../service/data/contract-excel-columns.service';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { PremisesContract } from '../../model/premises-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { PremisesContractCommandFactory } from '../../service/command/factory/premises-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { PremisesContractService } from '../../service/data/premises-contract.service';
import { ContractManagerPagedBase } from '../base/contract-manager-paged';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { PremisesLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';

@Component({
    selector: 'app-premises-contract-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['premises-contract-manager.component.scss', './../../../core/component/object-manager/object-manager.template.scss'],
    providers: [
        ContractExcelColumnsService,
        ApproveContractCommandHandler,
        ContractCommandHandlerResolver,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        PremisesLeaseAgreementListService,
        { provide: ContractService, useExisting: PremisesContractService },
        { provide: CommandFactoryService, useClass: PremisesContractCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PremisesContractManagerComponent extends ContractManagerPagedBase<PremisesContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    excelColumns: ContractExcelColumnsService,
    dispatcher: CommandDispatcher,
    handlerResolver: ContractCommandHandlerResolver,
    service: PremisesContractService,
    errorHandler: MessageService,
    userAccount: UserAccountService,
    navigationService: NavigationService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly companyService: CompanyService,
    private readonly propertyService: PropertyService,
    leaseAgreementListService: PremisesLeaseAgreementListService
  ) {
    super(
      service,
      dispatcher,
      handlerResolver,
      errorHandler,
      navigationService,
      userAccount,
      companyService,
      propertyService,
      activatedRoute,
      leaseAgreementListService
    );

    this.columnDefinition = columnDefinitionService.create('PremisesContract');
    this.preferencesKey = 'rent.premises-contract-manager.list';
    this.showExcelExportButton = true;
    this.excelFileName = 'Lokalkontrakt';
    this.excelContext = 'PremisesContract';
    this.excelAdditionalSheetItems = [
      { binding: 'additionalSheet.leaseAgreementRows', caption: 'EXCEL_EXPORT_SETTINGS.LEASE_AGREEMENT_ROWS', selected: false },
    ];
    this.excelColumnDefinition = [...this.columnDefinition];
    this.AddSubscription(excelColumns.create(this.excelContext, this.excelColumnDefinition).subscribe(columns => {
      // Keep custom columns that may have arrived while the article lists were loading.
      const bindings = new Set(this.excelColumnDefinition.map(column => column.binding));
      this.excelColumnDefinition.push(...columns.filter(column => !bindings.has(column.binding)));
    }));

    this.title = 'LEASE_AGREEMENT.PREMISES_LIST_HEADER';

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures) && account.hasKeyReceiptWriteAccess()) {
            this.addContextMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT', leaseAgreementCommand.print);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
        },
      })
    );
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  addFilterDefinitions(definition: any) {
    this._customPropertyService.getList().subscribe(customProperties => {
      super.addFilterDefinitions(definition);
      this.filterDefinition.push(
        new FilterDefinitionItem(
          'LEASE_AGREEMENT.POPULAR_NAME',
          'popularName',
          definition.popularName,
          null,
          definition.selectedFields.includes('popularName')
        )
      );

      const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
        customProperties,
        definition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building |
          CustomPropertyVisibilityType.Premises
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.columnDefinition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building |
          CustomPropertyVisibilityType.Premises
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.excelColumnDefinition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building |
          CustomPropertyVisibilityType.Premises
      );
      this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
    });
  }
}
